import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, Form, Input, Dropdown, Checkbox, Label } from "semantic-ui-react";
import { AppointmentMeHey } from "./AppointmentMeHeyUtils";
import { getAppointmentStatusInfo } from "./CardMakeAppointmentMeHey";
import { formatTimeFromString, formatTimeFromDate } from "react-lib/utils/dateUtils";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import ModalMakeAppointmentError from "./ModalMakeAppointmentError";
import CardAppointmentPackage from "../../HISV3/HCU/CardAppointmentPackage";

import "./PackageRightPanelMeHey.scss";

// ================ Types ================
type FormData = {
  appointmentId?: number;
  doctor?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  appointmentTime?: string;
  manualTime?: boolean;
  patientID?: string | null;
  patientName?: string;
  phoneNumber?: string;
};

type PackageRightPanelMeHeyProps = {
  setProp: any;
  onClose: () => void;
  editingAppointment?: AppointmentMeHey | null;
  selectedPatient?: Record<string, any>;
  selectedDoctorDSB?: (AppointmentMeHey & { dsb?: any }) | null;
  selectPackageBinder: any;
  onEvent: any;
  searchedItemListWithKey?: Record<string, any>;
  runSequence: any;
  MakeAppointmentMeHeySequence?: any;
  buttonLoadCheck: any;
  onAppointmentSuccess?: () => void;
};

// ================ Constants ================

const AMH = "AMH";
const KEY_PATIENT = `OfficialAccountPatientList_${AMH}`;

const DISABLED_STATUSES = ["ยกเลิกนัดหมาย", "ปฏิเสธโดย Provider", "เสร็จสิ้น"];

// ================ Helpers ================

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (const minute of [0, 30]) {
      const time = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      slots.push({ key: time, text: time, value: time });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const formatTime = (date: Date): string =>
  `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;

const formatDateDDMMYYYY = (date: Date): string =>
  `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;

const buildFormFromDSB = (
  selectedDoctorDSB: AppointmentMeHey & { dsb?: any }
): Pick<FormData, "doctor" | "date" | "startTime" | "endTime"> => {
  const dsb = selectedDoctorDSB.dsb;
  const { startTime, endTime } = selectedDoctorDSB;

  return {
    doctor: dsb.doctor_provider_id || "",
    date: formatDateDDMMYYYY(startTime),
    startTime: formatTime(startTime),
    endTime: formatTime(endTime),
  };
};

// ================ Component ================

const PackageRightPanelMeHey: React.FC<PackageRightPanelMeHeyProps> = (props) => {
  const [formData, setFormData] = useState<FormData>({
    doctor: "",
    date: "",
    startTime: "",
    endTime: "",
    appointmentTime: "",
    manualTime: false,
    patientID: null,
    patientName: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);

  const cardAppointmentPackageRef = useRef<any>(null);

  // ---- Derived values ----
  const generateDoctorSlots = (start: string, end: string, slotLength: number) => {
    const toMinutes = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };

    const toTimeString = (minutes: number) => {
      const h = String(Math.floor(minutes / 60)).padStart(2, "0");
      const m = String(minutes % 60).padStart(2, "0");
      return `${h}:${m}`;
    };

    const startMin = toMinutes(start);
    const endMin = toMinutes(end);

    const slots = [];

    for (let current = startMin; current <= endMin; current += slotLength) {
      slots.push({
        key: toTimeString(current),
        text: toTimeString(current),
        value: toTimeString(current),
      });
    }

    return slots;
  };

  const appointmentTimeSlots = useMemo(() => {
    const dsbData = props.selectedDoctorDSB?.dsb;
    if (
      !dsbData?.doctor_start_time ||
      !dsbData?.doctor_end_time ||
      !dsbData?.doctor_dsb_slot_length
    )
      return [];

    return generateDoctorSlots(
      dsbData?.doctor_start_time,
      dsbData?.doctor_end_time,
      dsbData?.doctor_dsb_slot_length
    );
  }, [props.selectedDoctorDSB]);

  const patientFullName = useMemo(() => {
    const detail = props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail;
    if (!detail) return "";
    return [detail.patient_pre_name, detail.patient_first_name, detail.patient_last_name]
      .filter(Boolean)
      .join(" ");
  }, [props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail]);

  const headerText = useMemo(() => {
    if (!props.selectedDoctorDSB?.startTime || !props.selectedDoctorDSB?.endTime) return "";

    const { startTime, endTime, title } = props.selectedDoctorDSB;
    const dateStr = formatDateDDMMYYYY(startTime);
    const startStr = `${startTime.getHours()}:${startTime
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
    const endStr = `${endTime.getHours()}.${endTime.getMinutes().toString().padStart(2, "0")}`;

    return `${dateStr} [${startStr} - ${endStr}] ${title || ""}`;
  }, [props.selectedDoctorDSB]);

  const statusInfo = useMemo(() => {
    if (!props.editingAppointment?.status) return null;
    return getAppointmentStatusInfo(props.editingAppointment.status);
  }, [props.editingAppointment?.status]);

  const isDisabledStatus = statusInfo ? DISABLED_STATUSES.includes(statusInfo.text) : false;

  const phoneNumber = useMemo(() => {
    if (props.editingAppointment) {
      return props.editingAppointment.tel || "-";
    }

    if (formData.patientID) {
      const patient = props.searchedItemListWithKey?.[KEY_PATIENT]?.find(
        (item: any) => item.id === formData.patientID
      );
      return patient?.phone_no || formData.phoneNumber || "-";
    }
    return "-";
  }, [props.editingAppointment, formData.patientID, props.searchedItemListWithKey]);

  // ---- Effects ----

  useEffect(() => {
    if (props.selectedPatient) {
      setFormData({
        ...formData,
        patientID: props.selectedPatient?.id,
        patientName: props.selectedPatient?.full_name,
        phoneNumber: props.selectedPatient?.main_contact_phone_no,
      });
    }

    if (!props.selectedDoctorDSB?.dsb) {
      setErrors({});
      return;
    }

    const base = buildFormFromDSB(props.selectedDoctorDSB);

    if (props.editingAppointment) {
      const appointmentTime = props.editingAppointment.estimated_at
        ? formatTimeFromDate(new Date(props.editingAppointment.estimated_at))
        : base.startTime;

      const isInSlots = appointmentTimeSlots.some((slot) => slot.value === appointmentTime);

      setFormData({
        ...base,
        appointmentId: props.editingAppointment.id,
        appointmentTime,
        manualTime: !isInSlots,
        patientID: props.MakeAppointmentMeHeySequence?.selectedAppointmentDetail?.patient || null,
        patientName: patientFullName,
        phoneNumber: props.editingAppointment.tel || "",
      });
    } else {
      setFormData({
        ...base,
        appointmentTime: formatTimeFromString(props.selectedDoctorDSB.dsb.start_time),
        manualTime: false,
        patientID: props.selectedPatient?.id || null,
        patientName: props.selectedPatient?.full_name || "",
        phoneNumber: props.selectedPatient?.main_contact_phone_no || "",
      });
    }

    setErrors({});
  }, [props.editingAppointment, props.selectedDoctorDSB, props.selectedPatient]);

  // ---- Handlers ----

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSelectedItem = useCallback(
    () => async (value: any) => {
      const patient = props.searchedItemListWithKey?.[KEY_PATIENT]?.find(
        (item: any) => item.id === value
      );

      if (patient) {
        const fullName = [patient.pre_name_th, patient.first_name_th, patient.last_name_th]
          .filter(Boolean)
          .join(" ");
        handleChange("patientName", fullName);
      }

      handleChange("patientID", value);
    },
    [props.searchedItemListWithKey]
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    if (!formData.patientName && !formData.patientID) newErrors.patientName = true;
    if (!formData.appointmentTime && !formData.manualTime) newErrors.appointmentTime = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await props.runSequence({
        sequence: "MakeAppointmentMeHey",
        action: "PACKAGE_APPOINTMENT",
        appointmentData: { ...formData },
        dsb: props.selectedDoctorDSB?.dsb || null,
        items: cardAppointmentPackageRef.current.items,
        checkedProductIds: cardAppointmentPackageRef.current.checkedProductIds,
        package: props.selectPackageBinder,
        onSuccess: props.onAppointmentSuccess,
      });
    } catch (error) {
      console.error("Error saving appointment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const mapOptions = useCallback(
    (items: any) =>
      items.map((item: any) => ({
        key: item.id,
        value: item.id,
        text: [item.pre_name_th, item.first_name_th, item.last_name_th].filter(Boolean).join(" "),
      })),
    []
  );

  return (
    <div className="package-right-panel-me-hey" style={{ padding: "2rem 1rem" }}>
      {/* Header Bar */}
      <div>
        <Label size="large">{headerText || "-"}</Label>
      </div>

      {/* Error Modal */}
      {props.MakeAppointmentMeHeySequence?.showError && (
        <ModalMakeAppointmentError
          open={props.MakeAppointmentMeHeySequence.showError}
          title="ไม่สามารถเพิ่มรายการนัดหมายใหม่ได้"
          subtitle={
            props.MakeAppointmentMeHeySequence.errorMessage || "กรุณาลองใหม่อีกครั้งในภายหลัง"
          }
          onClose={() => {
            props.runSequence({
              sequence: "MakeAppointmentMeHey",
              action: "CLOSE_ERROR_MODAL",
            });
          }}
        />
      )}

      {/* Form Content */}
      <div className="ui form" style={{ marginTop: "2rem" }}>
        <Form>
          {/* Appointment Time */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Field required error={errors.appointmentTime}>
              <label>เวลานัดหมาย</label>
              {formData.manualTime ? (
                <Input
                  fluid
                  type="time"
                  value={formData.appointmentTime}
                  style={{ width: "130px" }}
                  onChange={(e, { value }) => {
                    if (!isDisabledStatus) handleChange("appointmentTime", value);
                  }}
                  placeholder="HH:mm"
                />
              ) : (
                <Dropdown
                  fluid
                  selection
                  options={appointmentTimeSlots}
                  value={formData.appointmentTime}
                  onChange={(e, { value }) => handleChange("appointmentTime", value)}
                  {...(isDisabledStatus && { open: false })}
                />
              )}
            </Form.Field>
            <Checkbox
              label="กำหนดเวลาแบบ manual"
              checked={formData.manualTime}
              style={{ marginLeft: "1rem" }}
              onChange={(e, { checked }) => {
                if (!isDisabledStatus) {
                  handleChange("manualTime", checked);
                }
              }}
            />
          </div>

          {/* Patient Name */}
          <Form.Field required error={errors.patientName}>
            <label>ชื่อผู้รับบริการ</label>
            {props.editingAppointment ? (
              <Input value={patientFullName || "-"} readOnly />
            ) : (
              <SearchBoxDropdown
                onEvent={props.onEvent}
                type="OfficialAccountPatientList"
                id={AMH}
                style={{ width: "100%" }}
                fluid={true}
                useWithKey={true}
                inline={true}
                icon="search"
                placeholder="ค้นหาชื่อ - นามสกุล"
                limit={20}
                mapOptions={mapOptions}
                searchedItemListWithKey={props.searchedItemListWithKey}
                selectedItem={formData.patientID || null}
                defaultText={formData.patientName}
                setSelectedItem={handleSelectedItem()}
                onAdditionalUrlParams={() => ({ active: true })}
                disabled={true}
              />
            )}
          </Form.Field>

          {/* Phone Number */}
          <Form.Field>
            <label>เบอร์โทรติดต่อ</label>
            <Input value={phoneNumber} disabled={true} readOnly />
          </Form.Field>
        </Form>

        {/* --- */}
      </div>

      <CardAppointmentPackage
        ref={cardAppointmentPackageRef}
        setProp={props.setProp}
        // data
        data={props.selectPackageBinder?.items || []}
        buttonLoadCheck={props.buttonLoadCheck}
        // config
        selectable={true}
        isEdit={true}
        hideHistory={true}
        showQty={true}
        isHideQty={false}
        isPackageBinder={true}
        languageUX={props.languageUX}
      />

      {/* Footer Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <Button onClick={props.onClose} disabled={isLoading} onClose={props.onClose}>
          ปิดหน้าต่าง
        </Button>
        {!isDisabledStatus && (
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
            style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
          >
            {props.editingAppointment ? "แก้ไขนัดหมาย" : "บันทึกนัดหมาย"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PackageRightPanelMeHey;
